
import { useState } from 'react';
import { UseFormRegister, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormData } from './types';

interface CarveOutSettingsProps {
  register: UseFormRegister<FormData>;
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
}

export const CarveOutSettings = ({ register, setValue, getValues }: CarveOutSettingsProps) => {
  // Add local state to track switch states for immediate visual feedback
  const [switchStates, setSwitchStates] = useState<Record<string, boolean>>({
    allows_medical_carveout: getValues('allows_medical_carveout') === true,
    allows_ancillary_carveout: getValues('allows_ancillary_carveout') === true,
    allows_workers_comp_carveout: getValues('allows_workers_comp_carveout') === true,
    allows_epli_carveout: getValues('allows_epli_carveout') === true,
    allows_401k_carveout: getValues('allows_401k_carveout') === true,
  });

  const handleToggleChange = (id: keyof FormData, checked: boolean) => {
    console.log(`Toggling ${id} to ${checked}`);
    setValue(id, checked);
    
    // Update local state for immediate visual feedback
    setSwitchStates(prev => ({
      ...prev,
      [id]: checked
    }));
  };

  const CarveOutSwitch = ({ id, label, description }: { id: keyof FormData; label: string; description: string }) => {
    // Use the local state for visual feedback
    const isChecked = switchStates[id as string] || false;
    console.log(`Rendering switch ${id} with checked=${isChecked}`);
    
    return (
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label>{label}</Label>
          <p className="text-sm text-muted-foreground">
            {description}
          </p>
        </div>
        <Switch 
          checked={isChecked}
          onCheckedChange={(checked) => handleToggleChange(id, checked)}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Carve Out Settings</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Specify which benefits the PEO allows to be carved out from their standard offering.
      </p>
      
      <div className="grid gap-6">
        <CarveOutSwitch 
          id="allows_medical_carveout" 
          label="Medical Insurance Carve Out" 
          description="Can clients keep their existing medical insurance?"
        />
        <CarveOutSwitch 
          id="allows_ancillary_carveout" 
          label="Ancillary Benefits Carve Out" 
          description="Can clients keep their existing ancillary benefits (dental, vision, etc.)?"
        />
        <CarveOutSwitch 
          id="allows_workers_comp_carveout" 
          label="Workers Comp Carve Out" 
          description="Can clients keep their existing workers compensation policy?"
        />
        <CarveOutSwitch 
          id="allows_epli_carveout" 
          label="EPLI Carve Out" 
          description="Can clients keep their existing employment practices liability insurance?"
        />
        <CarveOutSwitch 
          id="allows_401k_carveout" 
          label="401(k) Carve Out" 
          description="Can clients keep their existing 401(k) plan?"
        />
      </div>
    </div>
  );
};
